import { useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import Spinner from "../widgets/Spinner"
import { useRedemption } from "../../redux/redemptionsSlice"
import { useSku } from "../../redux/skusSlice"
import { useLicense } from "../../redux/licensesSlice"
import { useProduct } from "../../redux/productsSlice"
import { useTopup } from "../../redux/topupsSlice"
import { licenseTypeLabels } from "../../api/models"
import { usePointTopup } from "../../redux/pointTopupsSlice"

function formatDate(d?: Date | string | null) {
  if (!d) return null
  const date = typeof d === "string" ? new Date(d) : d
  return date.toLocaleDateString()
}

export default function RedemptionDetail() {
  const { id } = useParams<{ id: string }>()
  const session = useSession()
  const accessToken = session.accessToken || ""

  const { data: redemption, status: redemptionStatus } = useRedemption(id!, accessToken)

  if (redemption) {
    return (
      <div className="page">
        <h1 className="page-title">Code Redemption</h1>
        <CopyableCode code={redemption.code} />

        <div className="detail-grid" style={{ marginTop: 24 }}>
          <div className="detail-item">
            <span className="detail-label">Content</span>
            <span className="detail-value"><SkuName skuId={redemption.skuId} /></span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Redeemed at</span>
            <span className="detail-value">{formatDate(redemption.redeemedAt) || "Not yet redeemed"}</span>
          </div>
        </div>

        {redemption.licenseIds.length > 0 && (
          <>
            <h2 className="section-title">Licenses</h2>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {redemption.licenseIds.map((licenseId) => (
                    <LicenseRow key={licenseId} licenseId={licenseId} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {redemption.topupIds.length > 0 && (
          <>
            <h2 className="section-title">Credits</h2>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Expire</th>
                  </tr>
                </thead>
                <tbody>
                  {redemption.topupIds.map((topupId) => (
                    <TopupRow key={topupId} topupId={topupId} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {redemption.pointTopupIds.length > 0 && (
          <>
            <h2 className="section-title">Points</h2>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Expire</th>
                  </tr>
                </thead>
                <tbody>
                  {redemption.pointTopupIds.map((pointTopupId) => (
                    <PointTopupRow key={pointTopupId} pointTopupId={pointTopupId} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    )
  }

  if (redemptionStatus === "error") {
    return (
      <div className="page">
        <Link to="/redemptions" className="back-link">&larr; All redemptions</Link>
        <p className="empty-state">Error loading redemption…</p>
      </div>
    )
  }

  return (
    <div className="page">
      <Link to="/redemptions" className="back-link">&larr; All redemptions</Link>
      <p className="empty-state"><Spinner size={16} /> Loading redemption…</p>
    </div>
  )
}

function SkuName({ skuId }: { skuId: string }) {
  const { data: sku } = useSku(skuId)

  if (!sku) {
    return <span><Spinner size={12} /> Loading…</span>
  }
  return <span>{sku.name}</span>
}

function LicenseRow({ licenseId }: { licenseId: string }) {
  const navigate = useNavigate()
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: license, status: licenseStatus } = useLicense(licenseId, accessToken)

  if (license) {
    return (
      <tr className="table-row-clickable" onClick={() => navigate(`/licenses/${licenseId}`)}>
        <td><LicenseProductName productId={license.productId} /></td>
        <td>{licenseTypeLabels[license.type]}</td>
        <td>{formatDate(license.expiresAt) || "Never"}</td>
      </tr>
    )
  }

  if (licenseStatus === "error") {
    return (
      <tr><td colSpan={3}><div className="empty-state">Error loading license</div></td></tr>
    )
  }

  return (
    <tr><td colSpan={3}><div className="empty-state"><Spinner size={12} /> Loading…</div></td></tr>
  )
}

function LicenseProductName({ productId }: { productId: string }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: product } = useProduct(productId, accessToken)

  if (!product) return <Spinner size={12} />
  return <span>{product.name}</span>
}

function TopupRow({ topupId }: { topupId: string }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: topup, status: topupStatus } = useTopup(topupId, accessToken)

  if (topup) {
    return (
      <tr>
        <td>{(topup.amount / 1e9).toFixed(2)}</td>
        <td>{formatDate(topup.expiresAt) || "Never"}</td>
      </tr>
    )
  }

  if (topupStatus === "error") {
    return (
      <tr><td colSpan={2}><div className="empty-state">Error loading topup</div></td></tr>
    )
  }

  return (
    <tr><td colSpan={2}><div className="empty-state"><Spinner size={12} /> Loading…</div></td></tr>
  )
}

function PointTopupRow({ pointTopupId }: { pointTopupId: string }) {
  const session = useSession()
  const accessToken = session.accessToken || ""
  const { data: pointTopup, status: pointTopupStatus } = usePointTopup(pointTopupId, accessToken)

  if (pointTopup) {
    return (
      <tr>
        <td>{pointTopup.amount}</td>
        <td>{formatDate(pointTopup.expiresAt) || "Never"}</td>
      </tr>
    )
  }

  if (pointTopupStatus === "error") {
    return (
      <tr><td colSpan={2}><div className="empty-state">Error loading point topup</div></td></tr>
    )
  }

  return (
    <tr><td colSpan={2}><div className="empty-state"><Spinner size={12} /> Loading…</div></td></tr>
  )
}

function CopyableCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  const formatted = code.match(/.{1,4}/g)?.join("-") ?? code

  const handleCopy = () => {
    navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="redemption-code" onClick={handleCopy} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleCopy()} title="Click to copy">
      <span className="redemption-code-text">{formatted}</span>
    </div>
  )
}
